import { Box, ButtonBase, useMediaQuery } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Pause, Play } from 'lucide-react';
import { useCallback, useEffect, useState, type ReactElement } from 'react';

import croissance from '@/assets/connexion/croissance.webp';
import fauteuil from '@/assets/connexion/fauteuil.webp';
import stockLait from '@/assets/connexion/stock-lait.webp';
import { Icon } from '@/ui/kit';

import { BrandPill } from './BrandPill';
import { FloatingCards } from './FloatingCards';

/**
 * Panneau illustre de l'ecran de connexion (variante « Q », maquette P01).
 *
 * Il est **decoratif et jamais necessaire a la comprehension** : les images ont
 * un `alt` vide, les cartes sont masquees aux technologies d'assistance, et
 * rien d'ecrit n'y figure hors la marque. Seules les commandes du diaporama
 * (pagination, pause) sont des controles, donc nommees.
 *
 * **Diaporama (WCAG 2.2.2, 2.3.3).** Il passe a l'image suivante toutes les 6 s
 * et s'arrete :
 * - d'office si la personne demande moins de mouvement (`prefers-reduced-motion`),
 *   sans jamais demarrer ;
 * - tant que la souris survole le panneau ou que le focus s'y trouve ;
 * - a la demande, par le bouton de pause, qui est la seule garantie offerte a qui
 *   ne survole ni ne focalise rien.
 *
 * **Chargement.** Seule la premiere image est demandee au rendu initial : les
 * deux autres ne sont montees qu'a leur premier affichage, puis gardees pour ne
 * pas se recharger au tour suivant. Une ouverture de la page ne paie donc
 * qu'environ 200 Ko d'illustration (WebP 900 px, alpha conserve) au lieu des
 * 7 Mo des trois PNG de la charte.
 *
 * **Echelle.** L'illustration et les cercles suivent la largeur du panneau
 * (unites `cqw` d'un conteneur) : la composition de la maquette, 700 px de large,
 * se reduit sans se deformer. Cartes et textes gardent leur taille de lecture.
 */

export const SLIDE_INTERVAL_MS = 6000;

interface Slide {
  readonly id: string;
  readonly src: string;
}

/** Diapositives proposees a la maquette : fauteuil (validee), croissance, stock de lait. */
const SLIDES: readonly Slide[] = [
  { id: 'fauteuil', src: fauteuil },
  { id: 'croissance', src: croissance },
  { id: 'stock-lait', src: stockLait },
];

/** Largeur de reference de la maquette : un pixel de la maquette vaut `px / 7` cqw. */
const cqw = (designPx: number): string => `${String(designPx / 7)}cqw`;

const FADE_MS = 600;

export const IllustrationPanel = (): ReactElement => {
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)', { noSsr: true });
  const [active, setActive] = useState(0);
  // Les images deja montrees : la premiere seulement au depart.
  const [shown, setShown] = useState<ReadonlySet<number>>(() => new Set([0]));
  const [loaded, setLoaded] = useState<ReadonlySet<number>>(() => new Set());
  const [userPaused, setUserPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);

  const playing = !reducedMotion && !userPaused && !hovered && !focused;

  const show = useCallback((index: number): void => {
    setActive(index);
    setShown((current) => (current.has(index) ? current : new Set(current).add(index)));
  }, []);

  // Une minuterie par diapositive : un changement manuel la relance, au lieu de
  // laisser une image s'effacer une seconde apres avoir ete choisie.
  useEffect(() => {
    if (!playing) {
      return undefined;
    }

    const timer = setTimeout(() => {
      show((active + 1) % SLIDES.length);
    }, SLIDE_INTERVAL_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [playing, active, show]);

  return (
    <Box
      component="section"
      aria-label="Illustrations"
      onMouseEnter={() => {
        setHovered(true);
      }}
      onMouseLeave={() => {
        setHovered(false);
      }}
      onFocus={() => {
        setFocused(true);
      }}
      onBlur={(event) => {
        // Le focus qui passe d'un controle du panneau a un autre n'en sort pas.
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setFocused(false);
        }
      }}
      sx={(theme) => ({
        position: 'relative',
        flex: '0 0 auto',
        alignSelf: 'center',
        width: 'min(700px, 48.6vw)',
        height: 'calc(100vh - 48px)',
        minHeight: 560,
        maxHeight: 900,
        overflow: 'hidden',
        borderRadius: '32px',
        containerType: 'inline-size',
        // Degrade de la maquette (70°, du rose pale au rose), compose sur les
        // jetons : la teinte de fin est le jeton d'action a 16 % sur la teinte de
        // depart, ce qui suit aussi le mode sombre.
        backgroundColor: theme.palette.tokens.accentPinkSoft,
        backgroundImage: `linear-gradient(70deg, transparent 0%, ${alpha(theme.palette.tokens.primary, 0.16)} 100%)`,
      })}
    >
      {/* Cercles concentriques, centres a 292 px du bas, et disque clair. */}
      {[520, 760, 1000].map((diameter, index) => (
        <Box
          key={diameter}
          aria-hidden
          sx={(theme) => ({
            position: 'absolute',
            left: '50%',
            bottom: cqw(292),
            width: cqw(diameter),
            height: cqw(diameter),
            transform: 'translate(-50%, 50%)',
            borderRadius: '50%',
            border: '1.5px solid',
            borderColor: alpha(
              theme.palette.mode === 'light' ? theme.palette.tokens.surface : theme.palette.tokens.primary,
              [0.5, 0.35, 0.22][index] ?? 0.2,
            ),
          })}
        />
      ))}
      <Box
        aria-hidden
        sx={(theme) => ({
          position: 'absolute',
          left: '50%',
          bottom: cqw(92),
          width: cqw(420),
          height: cqw(420),
          transform: 'translateX(-50%)',
          borderRadius: '50%',
          bgcolor: alpha(
            theme.palette.mode === 'light' ? theme.palette.tokens.surface : theme.palette.tokens.surfaceMuted,
            0.45,
          ),
        })}
      />

      {/* Illustrations : toutes empilees, seule l'active est opaque. */}
      {SLIDES.map((slide, index) => (
        <Box
          key={slide.id}
          aria-hidden
          data-slide={slide.id}
          data-active={index === active}
          sx={{
            position: 'absolute',
            left: cqw(59),
            bottom: cqw(-263.6),
            width: cqw(614.4),
            opacity: index === active && loaded.has(index) ? 1 : 0,
            transition: `opacity ${String(FADE_MS)}ms ease`,
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        >
          {shown.has(index) ? (
            <Box
              component="img"
              src={slide.src}
              alt=""
              decoding="async"
              onLoad={() => {
                setLoaded((current) => new Set(current).add(index));
              }}
              sx={{ display: 'block', width: '100%', height: 'auto' }}
            />
          ) : null}
        </Box>
      ))}

      <Box sx={{ position: 'absolute', top: 32, left: 32 }}>
        <BrandPill />
      </Box>

      <FloatingCards />

      {/* Pastille d'appui : les points se posent sur le bas de l'illustration, dont
          la creme leur ferait perdre leur contraste ; sur `surface` ils le gardent. */}
      <Box
        sx={{
          position: 'absolute',
          left: '50%',
          bottom: 16,
          transform: 'translateX(-50%)',
          display: 'flex',
          alignItems: 'center',
          px: 1,
          py: 0.5,
          borderRadius: '999px',
          bgcolor: 'tokens.surface',
          border: 1,
          borderColor: 'tokens.border',
        }}
      >
        {SLIDES.map((slide, index) => (
          <ButtonBase
            key={slide.id}
            aria-label={`Illustration ${String(index + 1)} sur ${String(SLIDES.length)}`}
            aria-current={index === active ? 'true' : undefined}
            onClick={() => {
              show(index);
            }}
            sx={{
              width: 24,
              height: 24,
              borderRadius: '12px',
              '&::before': {
                content: '""',
                display: 'block',
                height: 6,
                width: index === active ? 24 : 6,
                borderRadius: '3px',
                // Blanc sur ce rose pale ferait 1,1:1 : les points prennent
                // l'encre rose, et le point actif se distingue par sa largeur.
                bgcolor: 'tokens.accentPinkStrong',
                transition: 'width 200ms ease',
                '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
              },
            }}
          />
        ))}
        {reducedMotion ? null : (
          <ButtonBase
            // Libelle fixe et `aria-pressed` : l'etat ne doit etre annonce qu'une fois.
            aria-label="Mettre en pause le défilement des illustrations"
            aria-pressed={userPaused}
            onClick={() => {
              setUserPaused((current) => !current);
            }}
            sx={{
              ml: 0.5,
              width: 28,
              height: 28,
              borderRadius: '50%',
              color: 'tokens.accentPinkStrong',
            }}
          >
            <Icon icon={userPaused ? Play : Pause} size={14} />
          </ButtonBase>
        )}
      </Box>
    </Box>
  );
};
